import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useI18n } from '../i18n'
import { useAuth } from '../auth/AuthProvider'
import { GENDERS } from '@horsehub/shared'
import { useDiscover, useFeed, useFollowing, type DiscoverFilters } from '../api/hooks'
import { HorseCard } from '../components/HorseCard'
import { EmptyState, PageSpinner } from '../components/ui'
import { fmtDate } from '../lib/format'

export function Discover() {
  const { t, td } = useI18n()
  const { user } = useAuth()
  const [tab, setTab] = useState<'discover' | 'following'>('discover')

  return (
    <main className="mx-auto max-w-6xl px-5 py-8">
      <h1 className="text-3xl font-extrabold tracking-tight">{t('discover.title')}</h1>
      <p className="mt-1 text-brand-600 dark:text-brand-300">{t('discover.subtitle')}</p>

      {user && (
        <div className="mt-6 inline-flex rounded-full bg-brand-100 dark:bg-brand-800 p-1 text-sm font-semibold">
          <button onClick={() => setTab('discover')} className={`rounded-full px-5 py-2 transition ${tab === 'discover' ? 'bg-white dark:bg-brand-700 shadow-soft' : 'text-brand-600 dark:text-brand-300'}`}>{t('discover.tabDiscover')}</button>
          <button onClick={() => setTab('following')} className={`rounded-full px-5 py-2 transition ${tab === 'following' ? 'bg-white dark:bg-brand-700 shadow-soft' : 'text-brand-600 dark:text-brand-300'}`}>{t('discover.tabFollowing')}</button>
        </div>
      )}

      <div className="mt-8">
        {tab === 'discover' ? <DiscoverTab /> : <FollowingTab />}
      </div>
    </main>
  )

  function DiscoverTab() {
    const [filters, setFilters] = useState<DiscoverFilters>({})
    const { data: horses, isLoading } = useDiscover(filters)
    const set = (k: keyof DiscoverFilters) => (v: string) => setFilters({ ...filters, [k]: v || undefined })

    return (
      <>
        <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <input placeholder={t('discover.filterCountry')} value={filters.country || ''} onChange={(e) => set('country')(e.target.value)} className="rounded-xl border border-brand-200 dark:border-brand-700 bg-white dark:bg-brand-900 px-3 py-2 text-sm" />
          <input placeholder={t('discover.filterBreed')} value={filters.breed || ''} onChange={(e) => set('breed')(e.target.value)} className="rounded-xl border border-brand-200 dark:border-brand-700 bg-white dark:bg-brand-900 px-3 py-2 text-sm" />
          <input placeholder={t('discover.filterDiscipline')} value={filters.discipline || ''} onChange={(e) => set('discipline')(e.target.value)} className="rounded-xl border border-brand-200 dark:border-brand-700 bg-white dark:bg-brand-900 px-3 py-2 text-sm" />
          <select value={filters.gender || ''} onChange={(e) => set('gender')(e.target.value)} className="rounded-xl border border-brand-200 dark:border-brand-700 bg-white dark:bg-brand-900 px-3 py-2 text-sm">
            <option value="">{t('discover.anyGender')}</option>
            {GENDERS.map((g) => <option key={g} value={g}>{td('gender', g)}</option>)}
          </select>
        </div>
        <p className="mb-6 -mt-3 text-xs text-brand-500 dark:text-brand-400">{t('discover.filterHint')}</p>

        {isLoading ? (
          <PageSpinner />
        ) : horses && horses.length > 0 ? (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {horses.map((h) => <HorseCard key={h.id} to={`/h/${h.id}`} horse={h} />)}
          </div>
        ) : (
          <EmptyState icon="🔍" title={t('discover.emptyTitle')} body={t('discover.emptyBody')} />
        )}
      </>
    )
  }

  function FollowingTab() {
    const { t: tt, locale } = useI18n()
    const { data: followed } = useFollowing()
    const { data: feed, isLoading } = useFeed()

    return (
      <>
        {followed && followed.length > 0 && (
          <div className="mb-8 flex gap-3 overflow-x-auto pb-2">
            {followed.map((h) => (
              <Link key={h.id} to={`/h/${h.id}`} className="flex shrink-0 flex-col items-center gap-1">
                <div className="h-16 w-16 overflow-hidden rounded-full bg-brand-100 dark:bg-brand-800 ring-2 ring-brand-200 dark:ring-brand-700">
                  {h.profilePhotoUrl ? <img src={h.profilePhotoUrl} alt={h.name} className="h-full w-full object-cover" /> : <div className="grid h-full w-full place-items-center text-2xl">🐎</div>}
                </div>
                <span className="max-w-[4.5rem] truncate text-xs">{h.name}</span>
              </Link>
            ))}
          </div>
        )}

        {isLoading ? (
          <PageSpinner />
        ) : feed && feed.length > 0 ? (
          <div className="mx-auto max-w-2xl space-y-5">
            {feed.map((p) => (
              <article key={p.id} className="card overflow-hidden">
                {p.imageUrls?.[0] && <img src={p.imageUrls[0]} alt="" loading="lazy" className="h-64 w-full object-cover" />}
                <div className="p-5">
                  <Link to={`/h/${p.horseId}`} className="flex items-center gap-2 text-sm font-semibold text-brand-700 dark:text-brand-200 hover:underline">
                    {p.horseProfilePhotoUrl && <img src={p.horseProfilePhotoUrl} alt="" className="h-6 w-6 rounded-full object-cover" />}
                    {p.horseName}
                  </Link>
                  <h3 className="mt-2 text-lg font-bold">{p.title}</h3>
                  <div className="text-xs text-brand-500 dark:text-brand-400">{fmtDate(p.createdAt, locale)}</div>
                  <p className="mt-2 whitespace-pre-wrap text-sm text-brand-700 dark:text-brand-200">{p.body}</p>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <EmptyState icon="🐎" title={tt('discover.followingEmptyTitle')} body={tt('discover.followingEmptyBody')} action={<button onClick={() => setTab('discover')} className="btn-primary">{tt('discover.tabDiscover')}</button>} />
        )}
      </>
    )
  }
}
